import React from "react";
import { connect } from "react-redux";

import { IAppState } from "../../state/Store";
import MessageDetail from "../../types/system/MessageDetail";
import { Notification } from "../../types/system/Notification";
import { openMessage } from "../../state/system/Messaging";

import "./NotificationsMenu.scss";

interface NotificationsOverlayProps {
    messages: Array<MessageDetail>;

    openMessage: (message: MessageDetail) => void;
}

interface NotificationsOverlayState {
    displayed: Array<string>;
    displayNotifications: Array<Notification>;
}

class NotificationsOverlay extends React.Component<
    NotificationsOverlayProps,
    NotificationsOverlayState
> {
    state = {
        displayed: [],
        displayNotifications: [],
    };

    componentDidMount(): void {
        this.parseMessages();
    }

    componentDidUpdate(
        prevProps: Readonly<NotificationsOverlayProps>,
        prevState: Readonly<NotificationsOverlayState>,
        snapshot?: any,
    ): void {
        this.parseMessages();
    }

    isOpen(notification: Notification) {
        return !this.state.displayed.includes(notification.id);
    }

    finaliseNotifications(notification: Notification) {
        this.setState((prevState: NotificationsOverlayState) => ({
            displayed: [...prevState.displayed, notification.id],
        }));
    }

    parseMessages() {
        const newNotifications = this.props.messages
            .filter(
                message =>
                    !this.state.displayed.includes(message.Message.Id) &&
                    new Date().secondsBetween(message.CreatedOn.toDate()) < 180,
            )
            .map(message => {
                const result = {
                    id: message.Message.Id,
                    icon: "chat",
                    iconStyle: "fill",
                    title: message.SenderDetails,
                    text: message.BareMessage,
                    emphasize: false,
                    callback: () => {
                        this.finaliseNotifications(result);
                        this.props.openMessage(message);
                    },
                    date: message.CreatedOn.toDate(),
                };
                return result;
            });
        if (newNotifications.length > 0) {
            for (const notification of newNotifications) {
                if (
                    !this.state.displayNotifications.some(
                        n => n.id === notification.id,
                    )
                ) {
                    setTimeout(() => {
                        this.finaliseNotifications(notification);
                    }, 10000);
                }
            }
            if (
                newNotifications.some(
                    n =>
                        !this.state.displayNotifications.some(
                            dn => dn.id === n.id,
                        ),
                )
            )
                this.setState({
                    ...this.state,
                    displayNotifications: newNotifications,
                });
        }
        const newRead = this.state.displayNotifications.filter(
            notification =>
                !this.props.messages.some(
                    message => message.Message.Id === notification.id,
                ),
        );
        if (
            newRead.length > 0 &&
            newRead.some(n => !this.state.displayed.includes(n.id))
        )
            this.setState({
                ...this.state,
                displayed: [
                    ...this.state.displayed,
                    ...newRead.map(n => n.id),
                ].distinct(),
            });
    }

    render() {
        return (
            <div className={`notifications-overlay`}>
                {/* Individual notification toast items */}
                {this.state.displayNotifications.map((notification, index) => (
                    <div
                        key={index}
                        className={`notification${
                            this.isOpen(notification) ? " open" : ""
                        }`}
                        onClick={() => notification.callback?.()}>
                        {/* Remove/dismiss button */}
                        <div
                            className="remove"
                            onClickCapture={() =>
                                this.finaliseNotifications(notification)
                            }></div>

                        {/* Message icon */}
                        <span className="icon">
                            <i className={notification.iconStyle}>
                                {notification.icon}
                            </i>
                        </span>

                        {/* Notification details */}
                        <span className="detail">
                            {/* Sender name */}
                            <span className="sender">{notification.title}</span>
                            {/* Message preview */}
                            <span className="message">{notification.text}</span>
                        </span>
                    </div>
                ))}
            </div>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { messaging } = state.db;
    return {
        messages: messaging.unread,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        openMessage: (message: MessageDetail) => dispatch(openMessage(message)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(NotificationsOverlay);
